/**
 * ThreadV5NavRailConcept — a 6-noun left rail concept for Thread V5.
 *
 * Collapses the ~14-item production rail (ThreadV5NavRail) into six object-nouns:
 *   Runs · Design · Model · Trust · Project · Boards
 * Every existing V5View is still reachable: a noun opens its primary view, and the
 * active noun expands to show its member views as sub-items (progressive disclosure).
 * Provenance stays first-class as the "Trust" noun. A "Layouts" section shows the
 * user-composed-screen idea (the High-Lift workbench is the flagship example).
 *
 * The rail is a scroll container: when an expanded noun's tree overflows, hovering
 * near the top/bottom edge auto-scrolls it, the active noun is scrolled into view on
 * expand, and every (truncated) item shows its full label as a hover tooltip that is
 * portaled to <body> so it escapes the rail's scroll clip.
 *
 * Additive + non-destructive: toggled via `?rail=6` next to the real ThreadV5NavRail,
 * so the two can be compared live. Reuses thread-v5.css tokens; thread-v5-rail-concept.css
 * adds the noun-grouping / sub-item / scroll / tooltip styles.
 */
import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

import type { ThreadV4ViewSpec } from "../../api/threadV4Views";
import { Icons } from "../thread-v4/icons";
import type { V5View } from "./ThreadV5Workbench";
import "./thread-v5-rail-concept.css";

type Member = { view: V5View; label: string; authOnly?: boolean };
type Noun = { id: string; label: string; icon: ReactNode; tooltip: string; primary: V5View; members: Member[] };

const NOUNS: Noun[] = [
  {
    id: "runs",
    label: "Runs",
    icon: <Icons.GridIcon size={15} />,
    tooltip: "Imported runs, plots and flowfields — the operator home",
    primary: "workbench",
    members: [{ view: "workbench", label: "Run table & plots" }],
  },
  {
    id: "design",
    label: "Design",
    icon: <Icons.Cube size={15} />,
    tooltip: "Geometry engine — 2D sections, Wing, Aircraft",
    primary: "highlift",
    // FXC-8719: the geometry-design surfaces are auth-backed (their APIs 401 for
    // anonymous public viewers), so the whole noun is hidden for anon.
    members: [
      { view: "highlift", label: "2D Workbench", authOnly: true },
      { view: "wing-section", label: "Wing", authOnly: true },
      { view: "aircraft", label: "Aircraft", authOnly: true },
      { view: "control-surfaces", label: "Control surfaces", authOnly: true },
    ],
  },
  {
    id: "model",
    label: "Model",
    icon: <Icons.Sliders size={15} />,
    tooltip: "Build the aerodynamic database — surface, ADB, inputs",
    primary: "data",
    members: [
      { view: "data", label: "Surface · data table" },
      // "Surrogate · ADB Gen" and the old "Composite DB" are ONE surface
      // (AdbExplorerSurface; composite converged into the ADB Generator,
      // TRANSPORT.md §7, auto-detecting composite-resident projects). One menu
      // entry named for what people call it; ?view=composite still routes as an alias.
      { view: "tailor", label: "Aerodynamic Database" },
      { view: "filament-model", label: "Inputs · Filament", authOnly: true },
      { view: "training", label: "Adaptive sampling", authOnly: true },
      { view: "flow360-import", label: "Flow360 import", authOnly: true },
      { view: "artifacts", label: "Saved artifacts" },
    ],
  },
  {
    id: "trust",
    label: "Trust",
    icon: <Icons.Eye size={15} />,
    tooltip: "Never-flatten — is this value defensible? Pedigree, lineage, recovery",
    primary: "interpolant-audit",
    members: [
      { view: "interpolant-audit", label: "Pedigree · audit" },
      { view: "lineage", label: "Lineage graph" },
      { view: "selvedge", label: "From-plot · Selvedge" },
    ],
  },
  {
    id: "project",
    label: "Project",
    icon: <Icons.Folder size={15} />,
    tooltip: "Campaign — overview, branches & review",
    primary: "projects",
    members: [
      { view: "projects", label: "Overview", authOnly: true },
      { view: "branching", label: "Branches & review" },
      { view: "members", label: "Members", authOnly: true },
    ],
  },
  {
    id: "boards",
    label: "Boards",
    icon: <Icons.SavedPlots size={15} />,
    tooltip: "Dashboards & hand-off decks",
    primary: "home",
    members: [
      { view: "home", label: "Home" },
      { view: "dashboard-edit", label: "New board", authOnly: true },
    ],
  },
];

const VIEW_TO_NOUN = new Map<V5View, string>();
for (const noun of NOUNS) {
  for (const member of noun.members) {
    if (!VIEW_TO_NOUN.has(member.view)) VIEW_TO_NOUN.set(member.view, noun.id);
  }
}

// "composite" converged into the ADB Generator surface — one menu entry now
// ("Aerodynamic Database", view "tailor"). The back-compat ?view=composite route
// still resolves the same surface, so it highlights that entry.
const VIEW_ALIAS: Partial<Record<V5View, V5View>> = { composite: "tailor" };
const canonicalView = (v: V5View): V5View => VIEW_ALIAS[v] ?? v;

const WS_TOOLTIP = "Example user-composed layout - docks Design + Model panels (opens the 2D Workbench)";

export function ThreadV5NavRailConcept({
  activeView,
  activeDashboardName,
  dashboards,
  readOnly = false,
  onOpenView,
  onOpenDashboard,
  collapsed = false,
  onToggleCollapsed,
}: {
  activeView: V5View;
  activeDashboardName?: string | null;
  dashboards: ThreadV4ViewSpec[];
  readOnly?: boolean;
  onOpenView: (view: V5View) => void;
  onOpenDashboard: (view: ThreadV4ViewSpec) => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) {
  const visibleDashboards = dashboards.slice(0, 4);
  const activeDashboardKey = activeDashboardName ?? "";
  const dashboardIsActive = (dashboard: ThreadV4ViewSpec) =>
    activeView === "workbench" &&
    activeDashboardKey.length > 0 &&
    [dashboard.id, dashboard.slug, dashboard.name].includes(activeDashboardKey);
  const hasActiveDashboard = dashboards.some(dashboardIsActive);
  const activeNounId = hasActiveDashboard ? "boards" : VIEW_TO_NOUN.get(canonicalView(activeView)) ?? "runs";

  const railRef = useRef<HTMLElement | null>(null);
  const scrollVel = useRef(0);
  const rafId = useRef<number | null>(null);
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);

  // Edge auto-scroll: hovering near the rail's top/bottom scrolls it while it overflows.
  const tick = useCallback(() => {
    const el = railRef.current;
    if (el && scrollVel.current !== 0) {
      el.scrollTop += scrollVel.current;
      rafId.current = requestAnimationFrame(tick);
    } else {
      rafId.current = null;
    }
  }, []);

  const onRailMouseMove = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const el = railRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 1) {
        scrollVel.current = 0;
        return;
      }
      const rect = el.getBoundingClientRect();
      const zone = 56;
      const fromTop = event.clientY - rect.top;
      const fromBottom = rect.bottom - event.clientY;
      let velocity = 0;
      if (fromTop < zone) velocity = -Math.ceil(((zone - fromTop) / zone) * 9);
      else if (fromBottom < zone) velocity = Math.ceil(((zone - fromBottom) / zone) * 9);
      scrollVel.current = velocity;
      if (velocity !== 0 && rafId.current == null) rafId.current = requestAnimationFrame(tick);
    },
    [tick],
  );

  const onRailMouseLeave = useCallback(() => {
    scrollVel.current = 0;
    setTip(null);
  }, []);

  useEffect(() => () => {
    if (rafId.current != null) cancelAnimationFrame(rafId.current);
  }, []);

  // Keep the active (expanded) noun visible when it changes.
  useEffect(() => {
    const open = railRef.current?.querySelector<HTMLElement>(".v5-rail-noun.is-open");
    open?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [activeNounId]);

  const showTip = useCallback((event: MouseEvent<HTMLElement>, text: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTip({ text, x: rect.right + 8, y: rect.top + rect.height / 2 });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);

  return (
    <>
      <nav
        ref={railRef}
        className="v5-rail v5-rail-concept"
        aria-label="Thread V5 surfaces (6-noun concept)"
        data-testid="v5-surface-rail-concept"
        onMouseMove={onRailMouseMove}
        onMouseLeave={onRailMouseLeave}
      >
        {NOUNS.map((noun) => {
          const isActive = noun.id === activeNounId;
          const members = noun.members.filter((member) => !(readOnly && member.authOnly));
          // FXC-8719: a noun whose members are all auth-only (e.g. Design for
          // anonymous viewers) disappears entirely — its head would only open a
          // blocked view.
          if (members.length === 0) return null;
          return (
            <div className={`v5-rail-noun ${isActive ? "is-open" : ""}`} key={noun.id}>
              <button
                className={`v5-rail-item v5-rail-noun-head ${isActive ? "is-active" : ""}`}
                type="button"
                aria-current={isActive ? "page" : undefined}
                aria-label={`Open ${noun.label}`}
                onMouseEnter={(event) => showTip(event, noun.tooltip)}
                onMouseLeave={hideTip}
                onClick={() => onOpenView(noun.primary)}
              >
                {noun.icon}
                <span className="v5-rail-item-text">{noun.label}</span>
              </button>
              {isActive ? (
                <div className="v5-rail-subitems">
                  {members.map((member) => (
                    <button
                      key={member.view}
                      className={`v5-rail-subitem ${canonicalView(activeView) === member.view && !hasActiveDashboard ? "is-active" : ""}`}
                      type="button"
                      onMouseEnter={(event) => showTip(event, member.label)}
                      onMouseLeave={hideTip}
                      onClick={() => onOpenView(member.view)}
                    >
                      <span className="v5-rail-subitem-text">{member.label}</span>
                    </button>
                  ))}
                  {noun.id === "boards"
                    ? visibleDashboards.map((view) => {
                        const dashActive = dashboardIsActive(view);
                        return (
                          <button
                            key={view.id}
                            className={`v5-rail-subitem ${dashActive ? "is-active" : ""}`}
                            type="button"
                            aria-current={dashActive ? "page" : undefined}
                            onMouseEnter={(event) => showTip(event, view.name)}
                            onMouseLeave={hideTip}
                            onClick={() => onOpenDashboard(view)}
                          >
                            <span className="v5-rail-subitem-text">{view.name}</span>
                          </button>
                        );
                      })
                    : null}
                </div>
              ) : null}
            </div>
          );
        })}

        <span className="v5-rail-label">Layouts</span>
        {visibleDashboards.length > 0 ? (
          visibleDashboards.map((view) => {
            const dashActive = dashboardIsActive(view);
            return (
              <button
                key={view.id}
                className={`v5-rail-item v5-rail-ws ${dashActive ? "is-active" : ""}`}
                type="button"
                aria-current={dashActive ? "page" : undefined}
                onMouseEnter={(event) => showTip(event, view.name)}
                onMouseLeave={hideTip}
                onClick={() => onOpenDashboard(view)}
              >
                <Icons.Stack size={15} />
                <span className="v5-rail-item-text">{view.name}</span>
              </button>
            );
          })
        ) : readOnly ? null : (
          // FXC-8719: the High-Lift example layout opens an auth-backed surface,
          // so anonymous viewers don't get the fallback affordance.
          <button
            className="v5-rail-item v5-rail-ws"
            type="button"
            onMouseEnter={(event) => showTip(event, WS_TOOLTIP)}
            onMouseLeave={hideTip}
            onClick={() => onOpenView("highlift")}
          >
            <Icons.Stack size={15} />
            <span className="v5-rail-item-text">2D Workbench</span>
            <span className="v5-rail-ws-tag">you</span>
          </button>
        )}
        <button
          className="v5-rail-item v5-rail-ws is-ghost"
          type="button"
          onMouseEnter={(event) => showTip(event, "Compose your own layout by docking panels (coming soon)")}
          onMouseLeave={hideTip}
          disabled
        >
          <Icons.Plus size={15} />
          <span className="v5-rail-item-text">New layout</span>
        </button>
        {onToggleCollapsed ? (
          <button
            className={`v5-rail-item v5-rail-collapse-toggle ${collapsed ? "" : "is-pinned"}`}
            type="button"
            aria-label={collapsed ? "Pin sidebar open" : "Unpin sidebar"}
            aria-pressed={!collapsed}
            title={collapsed ? "Pin sidebar open" : "Unpin sidebar"}
            onMouseEnter={(event) => showTip(event, collapsed ? "Pin sidebar open" : "Unpin sidebar")}
            onMouseLeave={hideTip}
            onClick={onToggleCollapsed}
          >
            <Icons.Pin size={15} />
            <span className="v5-rail-item-text">{collapsed ? "Pin" : "Pinned"}</span>
          </button>
        ) : null}
      </nav>
      {tip
        ? createPortal(
            <div className="v5-rail-tip" style={{ left: tip.x, top: tip.y }} role="tooltip">
              {tip.text}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export default ThreadV5NavRailConcept;
